/*
 * varify-client-assign.js
 * ---------------------------------------------------------------------------
 * CLIENT-ID A/B ATTRIBUTION (companion to varify-order-stamp.js).
 *
 * WHY: the cart-attribute stamp (varify-order-stamp.js) misses ~20-25% of
 * experiment orders — express / Shop Pay / "Buy it now" / Recharge / cross-
 * session checkouts never re-write the cart attribute. This snippet closes that
 * gap the same way GA4 does: it records which arm a VISITOR (Shopify's
 * _shopify_y visitor token) was bucketed into, so a checkout_completed Web Pixel
 * can join {order -> clientId -> arm} server-side regardless of checkout path.
 *
 * FLOW:
 *   storefront (here):  POST {clientId=_shopify_y, experiment, arm}  -> /attr/assign
 *   Web Pixel:          POST {clientId, orderId}                     -> /attr/order
 *   backend:            join clientId -> arm, attribute the order
 *
 * Reads the SAME Varify assignment sources as varify-order-stamp.js
 * (localStorage "varify-experiment-<id>" + window.varify.appliedExperiments), so
 * it stays in lock-step with that script. Sends no PII — only the anonymous
 * Shopify visitor token + experiment/arm ids. Best-effort, fire-and-forget.
 *
 * Endpoint + key mirror the email-gate / A/B backend on Railway. The key is a
 * light anti-junk barrier only (it ships in client code, like the gate secret).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://trial-email-gate-production.up.railway.app/attr/assign';
  var KEY = '9103b747a890c7470246ad39e1ac8f69112bc3e8c34ed471';

  // DUAL-POST during the Echo migration. The A/B dashboard has been ported to
  // Echo and both systems are reconciled nightly against each other, so both
  // need the same assignments. Post to each independently: neither is allowed
  // to break the other, and rollback is deleting one of these two lines.
  //
  // Echo authenticates with the client's public site_key rather than a shared
  // secret. That is not a downgrade - the key carries the tenant, so a request
  // cannot land against the wrong brand, and it grants nothing but "record an
  // assignment for this client". The legacy KEY above is a global shared secret
  // with no tenant at all.
  //
  // Remove this block, and the Railway endpoint above, once the Node service is
  // decommissioned (§10 P8).
  var ECHO_ENDPOINT = 'https://api.echo.rawlings.us/api/ab/attr/assign';
  var ECHO_SITE_KEY = 'site_9Gh9ce7x-bcXdZeZr0bvcw';

  // Shopify's anonymous visitor token — equals the Web Pixel event.clientId,
  // which is how the two sides join server-side.
  function shopifyClientId() {
    var m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Current Varify assignments (expId -> variationId | "Original").
  function assignments() {
    var out = {};
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var k = window.localStorage.key(i);
        if (!k || k.indexOf('varify-experiment-') !== 0 || k.indexOf('exclusion') !== -1) continue;
        var exp = k.slice('varify-experiment-'.length);
        if (!exp) continue;
        var raw = window.localStorage.getItem(k), arm = raw;
        try {
          var p = JSON.parse(raw);
          if (p && typeof p === 'object' && 'variationId' in p) {
            arm = p.variationId == null ? 'Original' : String(p.variationId);
          }
        } catch (e) { /* non-JSON raw value — keep as-is */ }
        if (arm != null && arm !== '') out[exp] = String(arm);
      }
    } catch (e) { /* localStorage blocked */ }
    try {
      var applied = window.varify && window.varify.appliedExperiments;
      if (applied) {
        Object.keys(applied).forEach(function (exp) {
          var v = applied[exp] && applied[exp].variationId;
          if (v != null && out[exp] == null) out[exp] = String(v);
        });
      }
    } catch (e) { /* no runtime state */ }
    return out;
  }

  function send() {
    var cid = shopifyClientId();
    if (!cid) return; // no Shopify visitor token yet
    var a = assignments();
    var exps = Object.keys(a);
    if (!exps.length) return; // not bucketed into anything yet
    var sent = window.__abAssignSent || (window.__abAssignSent = {});
    exps.forEach(function (exp) {
      var mark = cid + ':' + exp + ':' + a[exp];
      if (sent[mark]) return; // send each assignment once per page load
      sent[mark] = 1;
      try {
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({ key: KEY, clientId: cid, experiment: exp, arm: a[exp] }),
        }).catch(function () {});
      } catch (e) { /* fire-and-forget */ }
      // Echo. Separate try/catch and its own .catch so a failure here cannot
      // affect the call above, and neither can affect the page.
      try {
        fetch(ECHO_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Site-Key': ECHO_SITE_KEY },
          keepalive: true,
          body: JSON.stringify({ clientId: cid, experiment: exp, arm: a[exp] }),
        }).catch(function () {});
      } catch (e) { /* fire-and-forget */ }
    });
  }

  // Varify applies after load; send shortly after, then retry once for late apply.
  if (document.readyState !== 'loading') setTimeout(send, 1200);
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(send, 1200); });
  setTimeout(send, 4000);
})();
